import { useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { useDaily, useNightAudit, useNightAuditRun } from '../api/queries';
import type { NightAuditSummary } from '../api/types';
import { addDays, formatDate, formatGhs } from '../format';
import { Badge, Button, Empty, ErrorNote, Loading, Modal, useReport, useToast } from '../ui/kit';
import { PageHead } from './shared';

function SummaryView({ s }: { s: NightAuditSummary }) {
  const rows = (o: Record<string, number>) => Object.entries(o).filter(([, v]) => v !== 0);
  return (
    <div className="stack">
      <div className="stats" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))' }}>
        <div className="stat">
          <span className="eyebrow">Revenue</span>
          <span className="value num">{formatGhs(s.totalRevenue)}</span>
        </div>
        <div className="stat">
          <span className="eyebrow">Arrivals</span>
          <span className="value num">{s.arrivals}</span>
        </div>
        <div className="stat">
          <span className="eyebrow">Departures</span>
          <span className="value num">{s.departures}</span>
        </div>
        <div className="stat">
          <span className="eyebrow">In house</span>
          <span className="value num">{s.inHouse}</span>
        </div>
      </div>
      {s.warnings.map((w) => (
        <div key={w} className="banner">
          {w}
        </div>
      ))}
      {[
        ['Revenue by account', rows(s.revenue)],
        ['Taxes and levies', rows(s.taxes)],
        ['Money received', rows(s.settlements)],
      ].map(([title, list]) =>
        (list as [string, number][]).length ? (
          <div key={title as string}>
            <div className="eyebrow" style={{ marginBottom: 6 }}>
              {title as string}
            </div>
            <table className="ledger">
              <tbody>
                {(list as [string, number][]).map(([k, v]) => (
                  <tr key={k}>
                    <td>{k}</td>
                    <td className="r num">{formatGhs(v)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null,
      )}
      <p className="small muted">
        Books and folios {s.reconciliation.balanced ? 'agree' : 'DO NOT agree'} ({formatGhs(s.reconciliation.guestLedger)} on both sides at closing).
      </p>
    </div>
  );
}

export function NightAudit() {
  const { can } = useAuth();
  const status = useNightAudit(true);
  const run = useNightAuditRun();
  const toast = useToast();
  const report = useReport();
  const [confirm, setConfirm] = useState(false);

  const next = status.data ? (status.data.closedThrough ? addDays(status.data.closedThrough, 1) : status.data.today) : undefined;
  const canClose = can('ledger.close_day') && !!status.data && !!next && next <= status.data.today;
  const preview = useDaily(next, can('reports.view') && !!next);

  return (
    <div className="page">
      <PageHead eyebrow="Accounts" title="Night audit" />

      {status.isLoading ? (
        <Loading />
      ) : status.error ? (
        <ErrorNote error={status.error} retry={() => void status.refetch()} />
      ) : (
        <>
          <section className="card stack" aria-labelledby="next">
            <div className="card-head" style={{ marginBottom: 0 }}>
              <h2 className="h2" id="next">
                Next day to close
              </h2>
              {status.data!.closedThrough ? <Badge tone="good">Closed through {formatDate(status.data!.closedThrough)}</Badge> : <Badge tone="warn">No day closed yet</Badge>}
            </div>
            {next ? <div className="display" style={{ fontSize: '1.6rem' }}>{formatDate(next, true)}</div> : null}
            <p className="small muted">Closing a day checks that the books match every folio, then freezes the day. Charges added later land on the next open day.</p>
            {preview.data ? (
              <p className="small">
                So far: revenue <strong className="num">{formatGhs(preview.data.totalRevenue)}</strong>, received <strong className="num">{formatGhs(preview.data.totalReceived)}</strong>, {preview.data.arrivals.length} arriving, {preview.data.departures.length} departing.
              </p>
            ) : null}
            {canClose ? (
              <Button variant="primary" onClick={() => setConfirm(true)}>
                Close {next ? formatDate(next) : 'the day'}
              </Button>
            ) : (
              <p className="small muted">{can('ledger.close_day') ? 'Every day up to today is already closed.' : 'Only a manager or accountant can close the day.'}</p>
            )}
          </section>

          <section aria-labelledby="hist" className="stack">
            <h2 className="h2" id="hist">
              Closed days
            </h2>
            {(status.data!.audits ?? []).length === 0 ? (
              <div className="card flat">
                <Empty icon="moon" title="No days closed yet" />
              </div>
            ) : (
              status.data!.audits.map((a, i) => (
                <details key={a.businessDate} className="card flat" open={i === 0}>
                  <summary style={{ cursor: 'pointer', fontWeight: 650 }}>
                    {formatDate(a.businessDate, true)} <span className="muted num">· {formatGhs(a.summary.totalRevenue)}</span>
                  </summary>
                  <div style={{ marginTop: 14 }}>
                    <SummaryView s={a.summary} />
                  </div>
                </details>
              ))
            )}
          </section>
        </>
      )}

      <Modal open={confirm} onClose={() => setConfirm(false)} title={`Close ${next ? formatDate(next, true) : ''}?`}>
        <div className="stack">
          <p>This freezes the day. Nothing can be posted to it afterwards, so make sure every payment and charge for the day has been entered.</p>
          <div className="modal-foot">
            <Button onClick={() => setConfirm(false)}>Not yet</Button>
            <Button
              variant="primary"
              loading={run.isPending}
              onClick={() =>
                run.mutate(next, {
                  onSuccess: () => {
                    toast.ok('Day closed');
                    setConfirm(false);
                  },
                  onError: (e) => {
                    setConfirm(false);
                    report(e);
                  },
                })
              }
            >
              Close the day
            </Button>
          </div>
        </div>
      </Modal>

    </div>
  );
}
